/** Pure link-target construction for the discrepancy → map deep-link (item 5).
 *
 *  A discrepancy row points at a platform record via `record_id`. When that
 *  record exists and its table maps to a map-selectable entity, we can deep-link
 *  the map to it (`/map?feature=<Type>:<id>`, read by MapPage into the detail
 *  panel; legacy `/?feature=` bookmarks are redirected there by App.tsx's
 *  HomeRoute — UX spec §3.8, D-3). Rows with no locatable platform record —
 *  e.g. `new_in_gis` (no platform row yet) or any row with a null `record_id` —
 *  return `null` so the UI renders plain text instead of a dead link. */

/** Table names that resolve to a rich detail view the map can select. */
const TABLE_TO_FEATURE_TYPE: Record<string, string> = {
  substations: 'Substation',
  feeders: 'Feeder',
  transformers: 'Transformer',
  meters: 'Meter',
}

export interface DiscrepancyLinkInput {
  table_name: string
  record_id: number | null
}

/** The map deep-link path for a discrepancy row, or `null` when the row has no
 *  locatable platform record. */
export function discrepancyMapLink(row: DiscrepancyLinkInput): string | null {
  const featureType = TABLE_TO_FEATURE_TYPE[row.table_name]
  if (!featureType || row.record_id == null) return null
  return `/map?feature=${featureType}:${row.record_id}`
}

/** Parse a `feature=<Type>:<id>` query-param value into a selectable feature
 *  descriptor, or `null` when malformed. Shared by App.tsx's handoff reader so
 *  the parse round-trips with {@link discrepancyMapLink}. */
export function parseFeatureParam(
  value: string | null | undefined,
): { _type: string; id: number } | null {
  if (!value) return null
  const idx = value.indexOf(':')
  if (idx <= 0) return null
  const type = value.slice(0, idx)
  const id = Number(value.slice(idx + 1))
  if (!type || !Number.isFinite(id)) return null
  return { _type: type, id }
}
